import "./style.scss";

import { TabContext, TabPanel } from "@mui/lab";
import { Tab } from "@mui/material";
import ImportData from "components/ImportData";
import type { TImportDataDictionary } from "components/ImportData/type";
import { getPendingImports } from "components/ImportData/utils";
import ModalBox from "components/Modal";
import ExpandedHelpdeskIcon from "components/shared/ExpandedHelpdeskIcon";
import GeneralInformation from "components/shared/GeneralInformation";
import ManagementBeneficiariesTable from "components/shared/ManagementBeneficiariesTable";
import ManagementGeneralFilter from "components/shared/ManagementGeneralFilter";
import ManagementGeneralTable from "components/shared/ManagementGeneralTable";
import DateComponent from "components/shared/ManagementPeriodComponent";
import SelectedRowsHandler from "components/shared/SelectedRowsHandler";
import StockFilter from "components/shared/StockFilter";
import StockPage from "components/StockPage";
import { ROLES } from "containers/ScopeContext/roles.enum";
import { useAuth } from "contexts/auth/AuthContext";
import { useTracker } from "contexts/tracker";
import { SmallBody, SmallBodyHighlight } from "helpers/TypographyComponents";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import useAPIPagination from "hooks/useAPIPagination";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useRoyaltiesRecalculationInProgress } from "hooks/useRoyaltiesRecalculationInProgress";
import usePageTitle from "hooks/usePageTitle";
import { usePreviousState } from "hooks/usePreviousState";
import useUserRole from "hooks/useUserRole";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useRef } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { getBeneficiaryTags } from "store/beneficiaryStore";
import { clearInvoice } from "store/bexioStore";
import { ProcessingJobStatus } from "store/jobsStore";
import type { GeneralTabTerm, TermState } from "store/managementStore";
import {
    downloadCustomizedExport,
    downloadDAPFile,
    downloadExpensesAccountingFile,
    fetchCustomExportActions,
} from "store/managementStore";
import type { PeriodState, TPeriod } from "store/periodStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TitleState } from "store/titleStore";
import { fetchTags } from "store/titleStore";
import { PeriodStatus } from "store/types";

import { StyledTabs } from "../shared/StyledTabs";
import { ManagementPeriodMenu } from "./ManagementPeriodMenu";
import { AddProvisions, ClosePeriod, Pending, SetMinimum } from "./ModalContent";
import RecomputeRoyaltiesButton from "./RecomputeRoyaltiesButton";
import TabButtons from "./TabButtons";
const TabPanelStylesSx = {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    marginTop: "15px",
};
export const buttonStyle = {
    fontSize: "12px",
    padding: "6px 16px !important",
    borderRadius: "3px",
    border: "1px solid #EAEAEA",
};

interface ICloseTermParams {
    periodId: number;
    selectedRows: number[];
}

type ManagementPropsActions = {
    getStats: (period: number) => void;
    closeTerm: (param: ICloseTermParams) => void;
    addSale: (titleId: number | null, termId: number) => Promise<unknown>;
    exportFile: () => void;
    exportBeneficiariesFile: () => void;
    exportStockFile: () => void;
    setCurrentPeriod: Dispatch<SetStateAction<TPeriod | undefined>>;
    /**
     * ID of the termPeriodData that is being filtered
     * ex: when navigating from the Advance Management page.
     */
    termPeriodData: number | undefined;
};

export enum ManagementTab {
    GENERAL = 0,
    BENEFICIARIES = 1,
    STOCK = 2,
}

type ManagementProps = Partial<PeriodState & TermState & TitleState> & ManagementPropsActions;
export type ModalPropType =
    | "close"
    | "import"
    | "importTerm"
    | "provisions"
    | "globalProvisions"
    | "minimum"
    | "withheldBeneficiaryGrossDuty";

export type AddSalesStatus = "idle" | "checking" | "import-in-progress" | "import-correction-needed";

const Management = ({
    currentPeriod,
    setCurrentPeriod,
    termPeriodData,
    periods = [],
    generalTabData = [],
    closeTermsStatus,
    stats,
    statsStatus,
    exportFileStatus,
    addSalesPendingStatus,
    addSalesCheckingStatus,
    getStats,
    closeTerm,
    addSale,
    exportFile,
    exportBeneficiariesFile,
    exportStockFile,
}: ManagementProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { userRole } = useUserRole();
    const { usesCegidGect } = useCompanySettings();
    // The backend zips the Cegid GECT file with the xlsx
    const expensesExportIsZip = usesCegidGect;
    const { isAdminCrealo } = useAuth();
    const tracker = useTracker();

    const savedTab = sessionStorage.getItem("management_tab");

    const periodStatus = currentPeriod?.isActive as PeriodStatus;
    const canAddSalesInClosedPeriod =
        periodStatus === PeriodStatus.CLOSED &&
        generalTabData.some((term) => Boolean(term.hasCollectiveManagementChannel));
    const periodDates = currentPeriod
        ? `${formatLocaleDateString({ date: currentPeriod.periodStart })} - ${formatLocaleDateString({
              date: currentPeriod.periodEnd,
          })}`
        : "";

    const currentPeriodId = currentPeriod?.id;

    const { companySettings } = useAppSelector((state) => state.settings);
    const usesStock = !!+companySettings?.settings?.find((sttg: any) => sttg.name === "uses_stock")?.content;
    const allowsSubscriptionChannels = !!+companySettings?.settings?.find(
        (sttg: any) => sttg.name === "allows_subscription_channels",
    )?.content;
    usePageTitle(t("Management.Header"));

    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [tabValue, setTabValue] = useState<number>(location?.state?.tab || +(savedTab || 0));
    const prevTab = usePreviousState(tabValue);
    const prevActivePeriod = usePreviousState(currentPeriod?.id);
    const [showModal, setShowModal] = useState<ModalPropType | null>(null);
    const [importType, setImportType] = useState<TImportDataDictionary | null>(null);
    const [termForProvision, setTermForProvision] = useState<number>();
    const tagTitleList = useAppSelector((state) => state.titles.tags);
    const tagBeneficiaryList = useAppSelector((state) => state.beneficiary.tags);
    const { termsLoadingStatus, benefInfoLoadingStatus } = useAppSelector((state) => state.management);
    const { jobs, jobsStatus } = useAppSelector((state) => state.jobs);
    const { locations, stockStatus } = useAppSelector((state) => state.stock);
    const { importDataStatus } = useAppSelector((state) => state.dataImport);
    const viewedPeriodId = currentPeriod?.id;
    const { isInProgress: isRoyaltiesRecalculating, pollingJobId } =
        useRoyaltiesRecalculationInProgress(viewedPeriodId);

    const addSalesButtonStatus = useMemo<AddSalesStatus>(() => {
        if (jobsStatus !== "fulfilled") {
            return "checking";
        }

        // multipart (history/imports/multipart) in progress => same as job in progress
        if (importDataStatus === "pending") {
            return "import-in-progress";
        }

        const salesPendingImports = getPendingImports({
            jobs,
            type: ["sales"],
        });

        return salesPendingImports.length > 0
            ? salesPendingImports.some(
                  (job) => job.status === ProcessingJobStatus.IDLE && job.metadata?.step === "corrections-required",
              )
                ? "import-correction-needed"
                : "import-in-progress"
            : "idle";
    }, [jobs, jobsStatus, importDataStatus]);

    const isSalesImportDisabled =
        isRoyaltiesRecalculating ||
        (["checking", "import-in-progress", "import-correction-needed"] as AddSalesStatus[]).includes(
            addSalesButtonStatus,
        );

    const salesImportTooltip = useMemo(() => {
        if (isRoyaltiesRecalculating) return t("Management.RecomputeRoyaltiesInProgress");
        if (addSalesButtonStatus === "import-in-progress") return t("Management.ImportInProgress");
        if (addSalesButtonStatus === "import-correction-needed")
            return t("Management.ImportInProgressCorrectionNeeded");
        return undefined;
    }, [isRoyaltiesRecalculating, addSalesButtonStatus, t]);

    const isInitialLoad = benefInfoLoadingStatus === "idle" || termsLoadingStatus === "idle";
    const isInitialLoadDone =
        benefInfoLoadingStatus === "succeeded" || termsLoadingStatus === "succeeded" || stockStatus === "succeeded";

    const {
        query,
        setQuery,
        limitPagination,
        chosenTitleTags,
        setChosenTitleTags,
        chosenBeneficiaryTags,
        setChosenBeneficiaryTags,
        chosenBrand,
        setChosenBrand,
        page,
        count,
        sortBy,
        setSortBy,
        sortOrder,
        setOffsetPagination,
        handleQuery,
        handleChangePage,
        handleChangeLimit,
        handleSort,
        handleRequest,
        control,
        setControl,
        setPaidStatus,
        setGrossMinimum,
        paidStatus,
        requestStatus,
    } = useAPIPagination({
        type: tabValue === 0 ? "terms" : tabValue === 1 ? "beneficiariesInfo" : "stock",
        sortOrder: tabValue === ManagementTab.BENEFICIARIES ? "desc" : "asc",
        isPaused: !isInitialLoadDone || isRoyaltiesRecalculating,
    });

    // Use ref to prevent side effects on useEffect when requestStatus change
    const requestStatusRef = useRef(requestStatus);
    useEffect(() => {
        requestStatusRef.current = requestStatus;
    }, [requestStatus]);

    const setModalProp = useCallback(
        (value: ModalPropType | null) => () => {
            setShowModal(value);
        },
        [],
    );

    const closeProvisionModal = useCallback(() => {
        setShowModal(null);
        setTermForProvision(undefined);
    }, []);

    const openProvisionModal = useCallback((term?: GeneralTabTerm) => {
        if (term) setTermForProvision(term.id);
        setShowModal("provisions");
    }, []);

    const closeTermForSelected = useCallback(
        (definedRows?: number[]) => {
            if (currentPeriod && closeTerm) {
                const selectedRowsParam = definedRows && definedRows.length ? definedRows : selectedRows;
                const closeTermParam: ICloseTermParams = {
                    periodId: currentPeriod.id,
                    selectedRows: selectedRowsParam,
                };
                closeTerm(closeTermParam);
            }
        },
        [closeTerm, currentPeriod, selectedRows],
    );
    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        sessionStorage.setItem("management_tab", newValue.toString());
    };

    const allChosenTags = useMemo(() => {
        const titleTagsObject = chosenTitleTags.map((tag) => {
            return { name: tag, type: "title" };
        });

        const beneficiaryTagsObject = chosenBeneficiaryTags.map((tag) => {
            return { name: tag, type: "beneficiary" };
        });

        return [...titleTagsObject, ...beneficiaryTagsObject];
    }, [chosenTitleTags, chosenBeneficiaryTags]);

    const selectedRowsAllUsePortal = useMemo(() => {
        if (selectedRows.length === 0) return false;
        const selectedIds = new Set(selectedRows);
        const selectedTerms = generalTabData.filter((term) => selectedIds.has(term.id));
        return selectedTerms.every((term) => term.accountStatus === "confirmed");
    }, [selectedRows, generalTabData]);

    const handleOnBeneficiaryTab = () => {
        setTabValue(1);
        setTimeout(() => {
            setGrossMinimum(">0");
            setPaidStatus("unpaid");
        }, 500);
    };

    useEffect(() => {
        const hasActiveFilters = !![...chosenBeneficiaryTags, ...chosenTitleTags].length;
        const hasChangedTab = prevTab !== tabValue;
        const hasChangedPeriod = prevActivePeriod !== currentPeriod?.id;
        if (hasActiveFilters || hasChangedTab || hasChangedPeriod) {
            setChosenTitleTags([]);
            setChosenBeneficiaryTags([]);
        }
        if (hasChangedTab || hasChangedPeriod) {
            setSelectedRows([]);
            // Clear query unless pre-filled from command palette or advance impact modal
            if (
                !localStorage.getItem("commandPaletteSearchQuery") &&
                !sessionStorage.getItem("management_initial_search")
            ) {
                setQuery("");
            }
            setSortBy("");
            setOffsetPagination(0);
            setPaidStatus("all");
            setGrossMinimum(undefined);
        }
    }, [
        tabValue,
        currentPeriod?.id,
        setChosenTitleTags,
        setChosenBeneficiaryTags,
        setOffsetPagination,
        setQuery,
        setSortBy,
        setPaidStatus,
        setGrossMinimum,
    ]);

    useEffect(() => {
        dispatch(fetchCustomExportActions());
    }, [dispatch]);

    useEffect(() => {
        if (!currentPeriodId || requestStatusRef.current === "pending") return;
        // If no TPD provided, do default fetch:
        if (isInitialLoad && !termPeriodData) {
            handleRequest({ isInitialLoad });
            // Otherwise, filter on correct TPD and periodId.
        } else {
            handleRequest({
                defaultType: "terms",
                extraOptions: { connectedTPD: termPeriodData, periodId: currentPeriodId },
            });
        }
    }, [isInitialLoad, currentPeriodId, termPeriodData]);

    useEffect(() => {
        if (tagTitleList === null) {
            dispatch(fetchTags());
        }
        if (tagBeneficiaryList === null) {
            dispatch(getBeneficiaryTags());
        }
    }, [dispatch, tagTitleList, tagBeneficiaryList]);

    const helpdeskOptions = [
        {
            text: t("Documentation.UnderstandGeneralView"),
            link: "https://helpdesk.crealo.app/fr/article/comprendre-lecran-de-gestion-la-vue-par-contrat-e1ss9e/",
        },
        {
            text: t("Documentation.UnderstandBeneficiaryView"),
            link: "https://helpdesk.crealo.app/fr/article/gerer-le-paiement-des-droits-depuis-lecran-gestion-beneficiaires-1wlsumt/?bust=1740763649375",
        },
        {
            text: t("Documentation.UnderstandStockView"),
            link: "https://helpdesk.crealo.app/fr/article/comprendre-lecran-de-gestion-le-stock-1418tzp/",
        },
    ];

    const resetPagination = () => {
        handleChangePage(undefined, 0);
        setOffsetPagination(0);
    };

    const exportDAP = () => {
        if (!currentPeriod) return;
        dispatch(downloadDAPFile({ periodId: currentPeriod.id }));
    };

    const exportAllowanceReport = () => {
        if (!currentPeriod) return;
        dispatch(
            downloadExpensesAccountingFile({
                periodId: currentPeriod.id,
                type: "allowance",
                isZip: expensesExportIsZip,
            }),
        );
    };

    const exportAdvanceReductionReport = () => {
        if (!currentPeriod) return;
        dispatch(
            downloadExpensesAccountingFile({
                periodId: currentPeriod.id,
                type: "advanceReduction",
                isZip: expensesExportIsZip,
            }),
        );
    };

    const customExportActions = useAppSelector((s) => s.management.customExportActions);

    const customExportMenuItems = useMemo(() => {
        return customExportActions.map((actionKey: string) => ({
            id: actionKey,
            name: actionKey,
            action: () => {
                if (!currentPeriod?.id) return;
                dispatch(downloadCustomizedExport({ periodId: currentPeriod.id, action: actionKey }));
            },
        }));
    }, [customExportActions, currentPeriod?.id, dispatch]);

    useEffect(() => {
        if (tracker) {
            const buttonName =
                tabValue === ManagementTab.GENERAL
                    ? "management-general"
                    : tabValue === ManagementTab.BENEFICIARIES
                      ? "management-beneficiaries"
                      : tabValue === ManagementTab.STOCK
                        ? "management-stock"
                        : null;

            if (buttonName) {
                tracker.trackEvent("Button clicked", {
                    button: buttonName,
                });
            }
        }
    }, [tabValue, tracker]);

    const showRecomputeRoyaltiesBtn =
        (periodStatus === PeriodStatus.FUTURE && userRole === ROLES.OWNER) ||
        (periodStatus === PeriodStatus.CURRENTLY_OPEN && isAdminCrealo);

    return (
        <>
            <div className="management-container">
                <TabContext value={tabValue.toString()}>
                    <div className="management-header">
                        <div className="buttons-group-one">
                            <div className="management-header-heading">{t("Management.Header")}</div>
                            <DateComponent
                                closeTermsStatus={closeTermsStatus}
                                currentPeriod={currentPeriod}
                                setCurrentPeriod={setCurrentPeriod}
                            />
                            {periodStatus === PeriodStatus.CURRENTLY_OPEN && (
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    {userRole !== ROLES.VIEW_ONLY && (
                                        <ManagementPeriodMenu onClosePeriod={setModalProp("close")} />
                                    )}
                                </div>
                            )}
                        </div>
                        {showRecomputeRoyaltiesBtn && currentPeriod && (
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <RecomputeRoyaltiesButton
                                    periodId={currentPeriod.id}
                                    isInProgress={isRoyaltiesRecalculating}
                                    pollingJobId={pollingJobId}
                                    onComplete={() => {
                                        if (currentPeriod) {
                                            getStats(currentPeriod.id);
                                            handleRequest();
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    <div className="management-general">
                        <div className="management-general-information">
                            <GeneralInformation stats={stats} statsStatus={statsStatus} />
                        </div>
                        <div className="tabs-container">
                            <StyledTabs value={+tabValue} onChange={handleChange} className="tabs-management">
                                <Tab label={t("Management.General")} style={{ marginRight: "20px" }} />
                                <Tab label={t("Management.Beneficiaries")} style={{ marginRight: "20px" }} />
                                {usesStock && <Tab label={t("Management.Stock")} style={{ marginRight: "0px" }} />}
                                <ExpandedHelpdeskIcon
                                    link={helpdeskOptions[tabValue].link}
                                    text={helpdeskOptions[tabValue].text}
                                />
                            </StyledTabs>
                            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                                {![0, 2147483647].includes(currentPeriod?.minimum ?? 0) && (
                                    <SmallBodyHighlight color="grey">
                                        {t("Management.MinimumDefined", {
                                            amount: formatNumber((currentPeriod?.minimum ?? 0) / 100, {
                                                isCurrency: true,
                                                always2Digit: true,
                                            }),
                                        })}
                                    </SmallBodyHighlight>
                                )}
                                {tabValue === ManagementTab.GENERAL && (
                                    <TabButtons
                                        title={t("Management.ImportDataSales")}
                                        companyId={companySettings?.id}
                                        tabType="general"
                                        tooltip={salesImportTooltip}
                                        setModalProp={setModalProp("globalProvisions")}
                                        onImport={(_, importType?: TImportDataDictionary) => {
                                            setModalProp("import")();
                                            importType && setImportType(importType);
                                        }}
                                        disabled={isSalesImportDisabled}
                                        isRoyaltiesRecalculating={isRoyaltiesRecalculating}
                                        canAddSalesInClosedPeriod={canAddSalesInClosedPeriod}
                                        exportGeneralFile={exportFile}
                                        exportBeneficiariesFile={exportBeneficiariesFile}
                                        exportStockFile={exportStockFile}
                                        periodStatus={periodStatus}
                                        selectedPeriod={currentPeriod}
                                        allowsSubscriptionChannels={allowsSubscriptionChannels}
                                        exportDAP={exportDAP}
                                        exportAdvanceReductionReport={exportAdvanceReductionReport}
                                        exportAllowanceReport={exportAllowanceReport}
                                        customExportMenuItems={customExportMenuItems}
                                    />
                                )}
                                {tabValue !== ManagementTab.GENERAL && (
                                    <TabButtons
                                        title={t("Stock.StockData")}
                                        companyId={companySettings?.id}
                                        tooltip={
                                            tabValue === 1 && !locations.length ? t("Stock.NoLocationsTooltip") : ""
                                        }
                                        disabled={tabValue === 1 && !locations.length}
                                        tabType={`${tabValue === 1 ? "beneficiaries" : "stock"}`}
                                        setModalProp={setModalProp("minimum")}
                                        onImport={(_, importType?: TImportDataDictionary) => {
                                            setModalProp("import")();
                                            importType && setImportType(importType);
                                        }}
                                        exportGeneralFile={exportFile}
                                        exportBeneficiariesFile={exportBeneficiariesFile}
                                        exportStockFile={exportStockFile}
                                        periodStatus={periodStatus}
                                        selectedPeriod={currentPeriod}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    {selectedRows.length !== 0 ? (
                        <SelectedRowsHandler
                            selectedRows={selectedRows}
                            tabValue={tabValue}
                            generalTabData={generalTabData}
                            closeTermsStatus={closeTermsStatus}
                            closeTerm={closeTermForSelected}
                            showProvisions={() => openProvisionModal()}
                            allUsePortal={selectedRowsAllUsePortal}
                            periodStatus={periodStatus}
                        />
                    ) : tabValue !== ManagementTab.STOCK ? (
                        <ManagementGeneralFilter
                            handleFilter={handleQuery}
                            filter={query}
                            titleTags={tagTitleList ?? []}
                            beneficiaryTags={tagBeneficiaryList ?? []}
                            exportFileStatus={exportFileStatus}
                            setChosenTitleTags={setChosenTitleTags}
                            chosenTitleTags={chosenTitleTags}
                            setChosenBeneficiaryTags={setChosenBeneficiaryTags}
                            chosenBeneficiaryTags={chosenBeneficiaryTags}
                            tabValue={tabValue}
                            exportFile={exportFile}
                            exportBeneficiariesFile={exportBeneficiariesFile}
                            exportStockFile={exportStockFile}
                            allChosenTags={allChosenTags}
                            setModalProp={(type: ModalPropType) => setShowModal(type)}
                            setChosenBrand={setChosenBrand}
                            setPaidStatus={setPaidStatus}
                            paidStatus={paidStatus}
                            resetPagination={resetPagination}
                        />
                    ) : (
                        <StockFilter
                            onImport={setModalProp("import")}
                            onExport={exportStockFile}
                            tags={tagTitleList}
                            onSelection={setChosenTitleTags}
                            onControlChange={setControl}
                            control={control}
                            onQuery={handleQuery}
                            filter={query}
                            setChosenBrand={setChosenBrand}
                        />
                    )}
                    {tabValue === ManagementTab.GENERAL && (
                        <TabPanel sx={TabPanelStylesSx} value="0">
                            <ManagementGeneralTable
                                page={page}
                                count={count}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                limitPagination={limitPagination}
                                handleChangePage={handleChangePage}
                                handleChangeLimit={handleChangeLimit}
                                handleSort={handleSort}
                                handleRequest={handleRequest}
                                selectedRows={selectedRows}
                                setSelectedRows={setSelectedRows}
                                generalTabData={generalTabData}
                                termsLoadingStatus={termsLoadingStatus}
                                periods={periods}
                                addSalesPendingStatus={addSalesPendingStatus}
                                addSalesCheckingStatus={addSalesCheckingStatus}
                                closeTermsStatus={closeTermsStatus}
                                activePeriod={currentPeriod?.id}
                                closeTerm={closeTermForSelected}
                                addSale={addSale}
                                getStats={getStats}
                                openProvisionModal={openProvisionModal}
                                periodStatus={periodStatus}
                                handleQuery={handleQuery}
                                isRoyaltiesRecalculating={isRoyaltiesRecalculating}
                            />
                        </TabPanel>
                    )}
                    {tabValue === ManagementTab.BENEFICIARIES && (
                        <TabPanel sx={TabPanelStylesSx} value="1">
                            <ManagementBeneficiariesTable
                                page={page}
                                count={count}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                limitPagination={limitPagination}
                                handleChangePage={handleChangePage}
                                handleChangeLimit={handleChangeLimit}
                                handleRequest={handleRequest}
                                handleSort={handleSort}
                                selectedRows={selectedRows}
                                setSelectedRows={setSelectedRows}
                                handleQuery={handleQuery}
                                periodStatus={periodStatus}
                                isRoyaltiesRecalculating={isRoyaltiesRecalculating}
                            />
                        </TabPanel>
                    )}
                    {usesStock && tabValue === ManagementTab.STOCK && (
                        <TabPanel sx={TabPanelStylesSx} value="2">
                            <StockPage
                                limitPagination={limitPagination}
                                handleChangePage={handleChangePage}
                                handleChangeLimit={handleChangeLimit}
                                page={page}
                                count={count}
                                control={control}
                                onControlChange={(value: any) => setControl(value)}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                handleSort={handleSort}
                                periodStatus={periodStatus}
                                handleQuery={handleQuery}
                                isRoyaltiesRecalculating={isRoyaltiesRecalculating}
                            />
                        </TabPanel>
                    )}
                </TabContext>
            </div>

            {showModal === "import" && importType && (
                <ModalBox
                    dictionaryKey={importType}
                    open={showModal === "import"}
                    onClose={() => {
                        setShowModal(null);
                        clearInvoice();
                        if (currentPeriod) {
                            handleRequest();
                            getStats(currentPeriod.id);
                        }
                    }}
                    mainTitle={t(`ImportData.Import_${importType}`)}
                >
                    <ImportData
                        dictionaryKey={importType}
                        isPeriodClosed={periodStatus === PeriodStatus.CLOSED}
                        close={() => {
                            setShowModal(null);
                            if (currentPeriod) {
                                handleRequest();
                                getStats(currentPeriod.id);
                            }
                        }}
                    />
                </ModalBox>
            )}

            <ModalBox
                open={showModal === "close"}
                onClose={setModalProp(null)}
                mainTitle={`${t("Management.ClosePeriod")} ${periodDates}`}
                subTitle={t("Management.ClosePeriodModal.ClosePeriodDescription")}
                helpdeskInfo={{
                    text: t("Management.ClosePeriodModal.Helpdesk"),
                    link: "https://helpdesk.crealo.app/fr/article/cloturer-une-periode-xhf5s5/",
                }}
            >
                <ClosePeriod close={setModalProp(null)} onBeneficiaryTab={handleOnBeneficiaryTab} />
            </ModalBox>

            <ModalBox
                open={showModal === "globalProvisions" || showModal === "provisions"}
                onClose={closeProvisionModal}
                mainTitle={t("Management.AddProvision")}
                subTitle={
                    selectedRows.length > 1 && showModal !== "globalProvisions"
                        ? t("Management.GroupAction")
                        : selectedRows.length <= 1 && showModal === "provisions"
                          ? t("Management.SimpleAction")
                          : showModal === "globalProvisions"
                            ? t("Management.GlobalAction")
                            : ""
                }
                helpdeskInfo={{
                    text: t("Documentation.ClickToReachDoc"),
                    link:
                        showModal === "provisions"
                            ? "https://helpdesk.crealo.app/fr/article/ajouter-une-provision-sur-un-titre-en-particulier-ykbknp/"
                            : "https://helpdesk.crealo.app/fr/article/ajouter-une-provision-globale-sur-la-periode-eh39da/",
                }}
            >
                <AddProvisions
                    key={`${showModal}-${(selectedRows.length === 0 ? [termForProvision] : selectedRows).join("-")}`}
                    typeModal={showModal}
                    selectedRows={selectedRows.length === 0 ? [termForProvision] : selectedRows}
                    terms={generalTabData}
                    close={() => {
                        closeProvisionModal();
                        if (currentPeriod) {
                            handleRequest();
                            getStats(currentPeriod.id);
                        }
                    }}
                />
            </ModalBox>
            <ModalBox
                open={showModal === "minimum"}
                onClose={setModalProp(null)}
                mainTitle={t("Management.SetMinimum")}
                subTitle={t("Management.GroupAction")}
            >
                <SetMinimum close={setModalProp(null)} />
            </ModalBox>

            <ModalBox open={closeTermsStatus === "pending"} onClose={() => {}} showCloseButton={false}>
                <Pending phrase={t("Management.ClosingTerms")} />
            </ModalBox>
        </>
    );
};

export default Management;
